import { ABOUT_SCROLL_TO_START, ABOUT_SECTION_IN_VIEW } from 'events';
import debounce from 'lodash/debounce';
import { component } from 'picoapp';
import Swiper, { A11y, FreeMode, Manipulation, Mousewheel, Scrollbar } from 'swiper';

import choozy from 'utils/choozy';

Swiper.use([A11y, Manipulation, Mousewheel, Scrollbar, FreeMode]);

// https://github.com/AndrewRayCode/easing-utils/blob/master/src/easing.js
function ease(t) {
  return Math.sin(t * (Math.PI / 2));
}

export default component((node, ctx) => {
  const {
    overviewAnimation,
    contentWrapper,
    scrollSnapBlock: scrollSnapBlocks = [],
  } = choozy(node);
  const maxDisplacement = 100;
  const bouncyImages = [...node.querySelectorAll('.bouncy-image')];
  const sections = [...node.querySelectorAll('.shopify-section')];
  const sectionIds = sections.map((section) => section.id);
  const { aboutUsParallaxOnScroll, aboutUsScrollSnap } = window.theme;
  let scrollWidth = node.scrollWidth - window.innerWidth;
  let dx = 0;
  let directionValue = 1;
  let lastScrollX = 0;
  let enableSnapScroll = false;
  let lastTouchStart = 0;

  if (!overviewAnimation && contentWrapper) {
    window.setTimeout(() => {
      if (!node.scrollLeft) contentWrapper.setAttribute('active', '');
    }, 2000);
  } else if (overviewAnimation) {
    window.setTimeout(() => {
      overviewAnimation.removeAttribute('active');
    }, 4800);

    return;
  }

  const snapScroll = debounce(() => {
    if (!enableSnapScroll || !aboutUsScrollSnap) return;

    enableSnapScroll = false;

    const { scrollLeft } = node;
    const sectionOffsets = sections
      .concat(scrollSnapBlocks)
      .map(
        (section) =>
          section.offsetLeft + (section.parentElement.closest('.shopify-section')?.offsetLeft || 0),
      );
    const startSectionOffset = sectionOffsets.reduce((prev, curr) =>
      Math.abs(curr - lastTouchStart) < Math.abs(prev - lastTouchStart) ? curr : prev,
    );
    const nearestSectionOffset = sectionOffsets.reduce((prev, curr) =>
      Math.abs(curr - scrollLeft) < Math.abs(prev - scrollLeft) &&
      curr !== startSectionOffset &&
      (directionValue === 1 ? curr < scrollLeft : curr > scrollLeft)
        ? curr
        : prev,
    );
    const nextSectionOffset =
      startSectionOffset === nearestSectionOffset
        ? sectionOffsets[sectionOffsets.indexOf(nearestSectionOffset) - directionValue]
        : nearestSectionOffset;

    if (Math.abs(scrollLeft - nextSectionOffset) <= window.innerWidth)
      node.scrollTo({
        left: nextSectionOffset,
        behavior: 'smooth',
      });

    lastTouchStart = 0;
  }, 100);

  const touchStartCallback = () => {
    lastTouchStart = node.scrollLeft;
    snapScroll.cancel();
  };

  const touchEndCallback = () => {
    if (lastTouchStart === node.scrollLeft) return;

    enableSnapScroll = true;
    snapScroll();
  };

  const updateProgressBar = (_e, scrollPosition = node.scrollLeft) => {
    const progress = (scrollPosition / scrollWidth) * 100;

    node.style.setProperty('--progress', `${progress}%`);
  };

  const transformScroll = (e) => {
    if (e.deltaY) node.scrollLeft += e.deltaY;
  };

  const wheelCallback = (e) => {
    const delta = e.deltaX + e.deltaY;

    if (Math.abs(delta) > 10) {
      if (delta < 0) {
        dx -= 4;
      } else {
        dx += 4;
      }
    }

    directionValue = dx / Math.abs(dx);

    if (dx > maxDisplacement) dx = maxDisplacement;
    else if (dx < -maxDisplacement) dx = -maxDisplacement;

    updateProgressBar(null, node.scrollLeft + delta);
  };

  const scrollCallback = () => {
    const { scrollLeft } = node;
    const scrollDiff = Math.abs(scrollLeft - lastScrollX);

    if (scrollDiff > 10) {
      if (scrollLeft > lastScrollX) {
        dx -= 2;
      } else {
        dx += 2;
      }
    }

    directionValue = dx / Math.abs(dx);

    if (dx > maxDisplacement) dx = maxDisplacement;
    else if (dx < -maxDisplacement) dx = -maxDisplacement;

    lastScrollX = scrollLeft;
    updateProgressBar(null, scrollLeft);
    snapScroll();
  };

  const updateBounce = (value) => {
    if (Math.abs(dx) > 1) {
      bouncyImages.forEach((image) => {
        image.style.setProperty('--bounce', `${value}px`);
      });
    }
  };

  function animate() {
    dx = Math.abs(dx) < 0.001 ? 0 : dx * 0.9;

    const bounce = ease(Math.abs(dx) / maxDisplacement) * maxDisplacement * -directionValue;

    updateBounce(bounce);
    window.requestAnimationFrame(animate);
  }

  const updateScrollWidth = debounce(() => {
    scrollWidth = node.scrollWidth - window.innerWidth;
  }, 100);

  const initListeners = () => {
    const { isMobile } = window.theme;

    if (aboutUsScrollSnap) {
      node[isMobile ? 'addEventListener' : 'removeEventListener']('touchstart', touchStartCallback);
      node[isMobile ? 'addEventListener' : 'removeEventListener']('touchend', touchEndCallback);
    }

    node[isMobile ? 'addEventListener' : 'removeEventListener'](
      'scroll',
      aboutUsParallaxOnScroll ? scrollCallback : updateProgressBar,
      { passive: true },
    );
    node[isMobile ? 'removeEventListener' : 'addEventListener'](
      'wheel',
      aboutUsParallaxOnScroll ? wheelCallback : updateProgressBar,
      { passive: true },
    );
    node[isMobile ? 'removeEventListener' : 'addEventListener']('wheel', transformScroll, {
      passive: true,
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const [mainSection] = entries.filter((entry) => entry.isIntersecting);

      if (mainSection) {
        const sectionId = mainSection.target.firstElementChild.id;

        ctx.emit(ABOUT_SECTION_IN_VIEW, null, {
          index: sectionIds.indexOf(mainSection.target.id),
          id: sectionId,
        });
      }
    },
    { root: null, threshold: 0 },
  );

  sections.forEach((section) => {
    observer.observe(section);
  });

  ctx.on(ABOUT_SCROLL_TO_START, () => {
    node.scrollTo({ left: 0, behavior: 'smooth' });
  });

  const mutationObserver = new MutationObserver(() => {
    updateScrollWidth();
  });

  updateScrollWidth();
  mutationObserver.observe(contentWrapper, { childList: true, subtree: true, attributes: false });
  initListeners();

  if (aboutUsParallaxOnScroll) window.requestAnimationFrame(animate);

  document.addEventListener('theme:screen:size-updated', updateScrollWidth);
  document.addEventListener('theme:screen:is-mobile-updated', () => {
    initListeners();
  });

  if (window.Shopify.designMode) {
    document.addEventListener('shopify:section:select', ({ target }) => {
      node.scrollTo({
        left: target.offsetLeft,
        behavior: 'smooth',
      });
    });
  }
});
